import { useCallback, useMemo, useRef } from "react";
import type { MutableRefObject } from "react";
import { isPollingSuspendedByAuth } from "@/lib/authExpiryGuard";
import {
  arePollingIntervalRefsRunning,
  clearPollingIntervalRefs,
  type PollingIntervals,
  type PollingSetupSource,
} from "./session-data-provider-utils";
import type {
  SessionPollingController,
  SessionPollingPollers,
} from "./session-polling.types";

interface UseSessionPollingControllerInput {
  pollers: SessionPollingPollers;
}

interface PollingIntervalRefs {
  sessionPollRef: MutableRefObject<NodeJS.Timeout | null>;
  membersPollRef: MutableRefObject<NodeJS.Timeout | null>;
  productsPollRef: MutableRefObject<NodeJS.Timeout | null>;
  chatPollRef: MutableRefObject<NodeJS.Timeout | null>;
  collabsPollRef: MutableRefObject<NodeJS.Timeout | null>;
  productReadsPollRef: MutableRefObject<NodeJS.Timeout | null>;
}

interface SchedulePollingIntervalsInput {
  refs: PollingIntervalRefs;
  pollers: SessionPollingPollers;
  intervals: PollingIntervals;
  source: PollingSetupSource;
}

export function useSessionPollingController({
  pollers,
}: UseSessionPollingControllerInput): SessionPollingController {
  const refs = useSessionPollingIntervalRefs();

  const startPolling = useCallback(
    (input: {
      intervals: PollingIntervals;
      source: PollingSetupSource;
    }): void => {
      const intervals = input.intervals;
      const source = input.source;
      schedulePollingIntervals({ refs, pollers, intervals, source });
    },
    [refs, pollers],
  );

  const clearPolling = useCallback((): void => {
    clearPollingIntervalRefs(toPollingRefList({ refs }));
  }, [refs]);

  const isPollingRunning = useCallback(
    (): boolean => arePollingIntervalRefsRunning(toPollingRefList({ refs })),
    [refs],
  );

  return { startPolling, clearPolling, isPollingRunning };
}

function useSessionPollingIntervalRefs(): PollingIntervalRefs {
  const sessionPollRef = useRef<NodeJS.Timeout | null>(null);
  const membersPollRef = useRef<NodeJS.Timeout | null>(null);
  const productsPollRef = useRef<NodeJS.Timeout | null>(null);
  const chatPollRef = useRef<NodeJS.Timeout | null>(null);
  const collabsPollRef = useRef<NodeJS.Timeout | null>(null);
  const productReadsPollRef = useRef<NodeJS.Timeout | null>(null);

  return useMemo(
    () => ({
      sessionPollRef,
      membersPollRef,
      productsPollRef,
      chatPollRef,
      collabsPollRef,
      productReadsPollRef,
    }),
    [
      sessionPollRef,
      membersPollRef,
      productsPollRef,
      chatPollRef,
      collabsPollRef,
      productReadsPollRef,
    ],
  );
}

const HIDDEN_VISIBILITY_STATE: DocumentVisibilityState = "hidden";

// A tick is skipped (not rescheduled — the interval keeps running) when the
// tab is hidden (a backgrounded session page polled 6 req/min for 10h straight
// during the 2026-07-06 incident) or while the auth-expiry guard is refreshing
// an expired token / has given up on it.
function shouldSkipPollTick(): boolean {
  if (
    typeof document !== "undefined" &&
    document.visibilityState === HIDDEN_VISIBILITY_STATE
  ) {
    return true;
  }
  return isPollingSuspendedByAuth();
}

function schedulePollingIntervals(input: SchedulePollingIntervalsInput): void {
  input.refs.sessionPollRef.current = setInterval(() => {
    if (shouldSkipPollTick()) return;
    void input.pollers.pollSessionOverview({
      source: input.source,
      intervalMs: input.intervals.session,
    });
  }, input.intervals.session);
  input.refs.membersPollRef.current = setInterval(() => {
    if (shouldSkipPollTick()) return;
    void input.pollers.pollMembers();
  }, input.intervals.members);
  input.refs.productsPollRef.current = setInterval(() => {
    if (shouldSkipPollTick()) return;
    void input.pollers.pollProducts();
  }, input.intervals.products);
  input.refs.chatPollRef.current = setInterval(() => {
    if (shouldSkipPollTick()) return;
    void input.pollers.pollChat();
  }, input.intervals.chat);
  input.refs.collabsPollRef.current = setInterval(() => {
    if (shouldSkipPollTick()) return;
    void input.pollers.pollCollabs();
  }, input.intervals.collabs);
  input.refs.productReadsPollRef.current = setInterval(() => {
    if (shouldSkipPollTick()) return;
    void input.pollers.pollProductReads();
  }, input.intervals.chat);
}

function toPollingRefList(input: {
  refs: PollingIntervalRefs;
}): readonly MutableRefObject<NodeJS.Timeout | null>[] {
  return [
    input.refs.sessionPollRef,
    input.refs.membersPollRef,
    input.refs.productsPollRef,
    input.refs.chatPollRef,
    input.refs.collabsPollRef,
    input.refs.productReadsPollRef,
  ];
}
